/**
 * Граница ошибок вокруг рабочей области.
 *
 * ── Зачем ───────────────────────────────────────────────────────────────────
 *
 * Без неё любая необработанная ошибка в любом компоненте снимает ВЕСЬ экран:
 * React размонтирует дерево целиком и оставляет белый лист. Для рабочего
 * инструмента это худший из возможных исходов — человек в середине переписки
 * теряет не панель, а всё приложение, и единственное, что ему остаётся, —
 * перезагрузка.
 *
 * Такое уже случалось дважды на ровном месте: достаточно ответа сервера, где
 * не оказалось ожидаемого поля. Защищать каждое обращение к каждому полю
 * невозможно и не нужно — нужна страховка на случай, когда защититься забыли.
 *
 * ── Почему не глобально на всё приложение ───────────────────────────────────
 *
 * Граница ставится вокруг СОДЕРЖИМОГО раздела, а не вокруг корня. Шапка с
 * переключением разделов должна пережить падение: иначе человеку некуда уйти
 * с упавшего экрана, и он снова остаётся с одной кнопкой «перезагрузить».
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertIcon } from "./icons.tsx";

interface Props {
  children: ReactNode;
  /**
   * Меняется при переходе в другой раздел.
   *
   * По смене ключа граница сбрасывается: упавший раздел не должен оставаться
   * упавшим после того, как человек ушёл и вернулся.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // В консоль — намеренно: без стека разбирать такие падения нечем, а
    // отправлять их на сервер пока некуда.
    console.error("[ui] раздел упал:", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="grid h-full place-items-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center shadow-md">
          <span
            aria-hidden
            className="grid size-11 place-items-center rounded-2xl bg-destructive/10 text-destructive"
          >
            <AlertIcon className="size-6" />
          </span>

          <p className="text-[15px] font-semibold text-heading">
            Раздел не отобразился
          </p>

          <p className="text-sm leading-relaxed text-muted-foreground">
            Остальное приложение работает — можно перейти в другой раздел через
            шапку. Если повторится, сообщите: в консоли есть подробности.
          </p>

          {/* Текст ошибки показываем: он часто прямо называет поле, которого
              не хватило, и это экономит разбор. */}
          <code className="max-w-full overflow-hidden text-ellipsis rounded-lg bg-secondary px-2.5 py-1.5 text-[11px] text-muted-foreground">
            {this.state.error.message}
          </code>

          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="rounded-full border-transparent bg-primary px-4 py-1.5 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }
}
